import React, { useId } from "react";
import { Navigation } from "lucide-react";

const chartVars = {
  "--surface-1": "hsl(var(--ivory))",
  "--surface-2": "hsl(var(--card))",
  "--border": "hsla(222, 47%, 7%, 0.1)",
  "--border-strong": "hsla(222, 47%, 7%, 0.18)",
  "--text-accent": "hsl(var(--emerald))",
  "--text-primary": "hsl(var(--ink))",
  "--text-secondary": "hsl(var(--ink-soft))",
  "--text-muted": "hsla(222, 24%, 18%, 0.55)",
  "--text-success": "hsl(var(--emerald))",
  "--bg-success": "hsla(217, 71%, 35%, 0.1)",
  "--fill-success": "hsl(var(--emerald))",
  "--fill-accent": "hsl(var(--emerald))",
};

export const ShipRouteTracker = () => {
  const uid = useId().replace(/:/g, "");
  const landTex = `land-tex-${uid}`;
  const waterTex = `water-tex-${uid}`;
  const curr = `curr-${uid}`;

  return (
    <div
      className="relative rounded-2xl border border-ink-10 overflow-hidden p-3 md:p-4 bg-[hsl(var(--ivory))]"
      style={chartVars}
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="inline-flex items-center gap-2 bg-white border border-ink-10 rounded-full px-3.5 py-2">
          <Navigation
            className="h-4 w-4 text-emerald shrink-0"
            style={{ transform: "rotate(168deg)" }}
            aria-hidden
          />
          <span className="text-[13px] font-mono ink">168°</span>
          <span className="w-px h-3.5 bg-[hsla(222,47%,7%,0.18)]" />
          <span className="text-[13px] font-mono ink">14.2 kn</span>
        </div>
        <div className="inline-flex items-center gap-1.5 bg-emerald/10 rounded-full px-3 py-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald" />
          <span className="text-xs text-emerald font-medium">Underway</span>
        </div>
      </div>

      <svg width="100%" viewBox="60 30 620 540" preserveAspectRatio="xMidYMid meet" role="img" className="block w-full max-h-[260px]" aria-labelledby={`${uid}-title`}>
        <title id={`${uid}-title`}>Detailed nautical chart of the ship route</title>
        <desc>
          Chart showing Turkey, Egypt, Sudan, Saudi Arabia, Yemen, Somalia and Kenya with coastlines,
          a latitude and longitude grid, secondary ports, and the route from Mersin through the Suez
          Canal and Red Sea to Mombasa
        </desc>
        <defs>
          <pattern id={landTex} width="10" height="10" patternUnits="userSpaceOnUse">
            <circle cx="1.4" cy="1.4" r="0.9" fill="var(--border-strong)" />
          </pattern>
          <pattern id={waterTex} width="26" height="26" patternUnits="userSpaceOnUse">
            <circle cx="2" cy="2" r="0.8" fill="var(--border)" opacity="0.5" />
          </pattern>
          <marker
            id={curr}
            viewBox="0 0 10 10"
            refX="8"
            refY="5"
            markerWidth="5"
            markerHeight="5"
            orient="auto-start-reverse"
          >
            <path
              d="M2 1L8 5L2 9"
              fill="none"
              stroke="context-stroke"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </marker>
        </defs>

        <rect x="0" y="0" width="700" height="700" fill={`url(#${waterTex})`} />

        <g opacity="0.35">
          {[120, 260, 400, 540].map((y) => (
            <line key={`h-${y}`} x1="0" y1={y} x2="700" y2={y} stroke="var(--border)" strokeWidth="0.5" strokeDasharray="2 6" />
          ))}
          {[180, 360, 540].map((x) => (
            <line key={`v-${x}`} x1={x} y1="0" x2={x} y2="700" stroke="var(--border)" strokeWidth="0.5" strokeDasharray="2 6" />
          ))}
        </g>
        <g fill="var(--text-muted)" className="font-mono text-[10px]">
          <text x="6" y="116">36°N</text>
          <text x="6" y="256">24°N</text>
          <text x="6" y="396">12°N</text>
          <text x="6" y="536">0°</text>
          <text x="164" y="14">30°E</text>
          <text x="344" y="14">40°E</text>
          <text x="524" y="14">50°E</text>
        </g>

        <path
          d="M 260,120 L 300,80 L 360,55 L 430,50 L 500,60 L 560,85 L 592,120 L 572,152 L 512,142 L 452,152 L 400,140 L 350,157 L 300,150 L 268,140 Z"
          fill="var(--surface-2)"
          stroke="var(--border-strong)"
          strokeWidth="0.5"
        />
        <path
          d="M 260,120 L 300,80 L 360,55 L 430,50 L 500,60 L 560,85 L 592,120 L 572,152 L 512,142 L 452,152 L 400,140 L 350,157 L 300,150 L 268,140 Z"
          fill={`url(#${landTex})`}
        />
        <text x="410" y="95" fill="var(--text-secondary)" fontStyle="italic" className="font-mono text-[10px]">Turkey</text>

        <path
          d="M 130,220 L 220,205 L 300,200 L 362,214 L 410,236 L 431,250 L 415,300 L 440,340 L 460,390 L 481,428 L 470,478 L 490,518 L 478,560 L 458,602 L 398,612 L 328,596 L 258,560 L 198,510 L 158,450 L 133,380 L 118,300 L 124,250 Z"
          fill="var(--surface-2)"
          stroke="var(--border-strong)"
          strokeWidth="0.5"
        />
        <path
          d="M 130,220 L 220,205 L 300,200 L 362,214 L 410,236 L 431,250 L 415,300 L 440,340 L 460,390 L 481,428 L 470,478 L 490,518 L 478,560 L 458,602 L 398,612 L 328,596 L 258,560 L 198,510 L 158,450 L 133,380 L 118,300 L 124,250 Z"
          fill={`url(#${landTex})`}
        />
        <text x="200" y="255" fill="var(--text-secondary)" fontStyle="italic" className="font-mono text-[10px]">Egypt</text>
        <text x="185" y="390" fill="var(--text-secondary)" fontStyle="italic" className="font-mono text-[10px]">Sudan</text>
        <text x="330" y="430" fill="var(--text-secondary)" fontStyle="italic" className="font-mono text-[10px]">Somalia</text>
        <text x="255" y="565" fill="var(--text-secondary)" fontStyle="italic" className="font-mono text-[10px]">Kenya</text>

        <path
          d="M 462,162 L 540,146 L 610,166 L 646,222 L 640,292 L 610,352 L 560,396 L 520,406 L 500,370 L 480,320 L 465,270 L 456,222 Z"
          fill="var(--surface-2)"
          stroke="var(--border-strong)"
          strokeWidth="0.5"
        />
        <path
          d="M 462,162 L 540,146 L 610,166 L 646,222 L 640,292 L 610,352 L 560,396 L 520,406 L 500,370 L 480,320 L 465,270 L 456,222 Z"
          fill={`url(#${landTex})`}
        />
        <text x="530" y="230" fill="var(--text-secondary)" fontStyle="italic" className="font-mono text-[10px]">Saudi Arabia</text>
        <text x="550" y="365" fill="var(--text-secondary)" fontStyle="italic" className="font-mono text-[10px]">Yemen</text>

        <text x="480" y="185" fill="var(--text-muted)" fontStyle="italic" className="font-mono text-[10px]">Mediterranean Sea</text>
        <text x="490" y="300" fill="var(--text-muted)" fontStyle="italic" className="font-mono text-[10px]">Red Sea</text>
        <text x="548" y="445" fill="var(--text-muted)" fontStyle="italic" className="font-mono text-[10px]">Gulf of Aden</text>
        <text x="560" y="600" fill="var(--text-muted)" fontStyle="italic" className="font-mono text-[10px]">Indian Ocean</text>

        <g opacity="0.5" stroke="var(--text-muted)" strokeWidth="1" fill="none">
          <path d="M 610,540 L 630,532" markerEnd={`url(#${curr})`} />
          <path d="M 90,520 L 108,510" markerEnd={`url(#${curr})`} />
        </g>

        <circle cx="330" cy="205" r="3" fill="var(--text-muted)" />
        <text x="337" y="209" fill="var(--text-secondary)" className="font-mono text-[10px]">Alexandria</text>
        <circle cx="480" cy="270" r="3" fill="var(--text-muted)" />
        <text x="487" y="274" fill="var(--text-secondary)" className="font-mono text-[10px]">Jeddah</text>
        <circle cx="440" cy="600" r="3" fill="var(--text-muted)" />
        <text x="400" y="622" fill="var(--text-secondary)" className="font-mono text-[10px]">Dar es Salaam</text>

        <path
          d="M 430,148 C 420,190 425,215 430,248"
          fill="none"
          stroke="var(--text-muted)"
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray="1 8"
          opacity="0.55"
        />
        <path
          d="M 430,248 C 448,280 456,320 465,352 C 472,375 480,388 492,401 C 505,418 500,430 500,430 C 490,450 483,480 471,518 C 466,535 472,545 475,555"
          fill="none"
          stroke="var(--text-accent)"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeDasharray="1 9"
        />

        <g transform="translate(492,401) rotate(168)">
          <path d="M 0,-9 L 6,7 L 0,3 L -6,7 Z" fill="var(--fill-accent)" stroke="var(--surface-1)" strokeWidth="1" />
        </g>

        <g>
          <circle cx="430" cy="148" r="6" fill="var(--surface-1)" stroke="var(--text-primary)" strokeWidth="2" />
          <rect x="452" y="130" width="86" height="34" rx="17" fill="var(--surface-2)" stroke="var(--border)" strokeWidth="0.5" />
          <text x="495" y="151" textAnchor="middle" fill="var(--text-primary)" className="text-[14px] font-medium">Mersin</text>
        </g>

        <g>
          <circle cx="430" cy="248" r="6" fill="var(--surface-1)" stroke="var(--text-primary)" strokeWidth="2" />
          <rect x="306" y="230" width="76" height="34" rx="17" fill="var(--surface-2)" stroke="var(--border)" strokeWidth="0.5" />
          <text x="344" y="251" textAnchor="middle" fill="var(--text-primary)" className="text-[14px] font-medium">Suez</text>
        </g>

        <g>
          <circle cx="475" cy="555" r="9" fill="var(--text-accent)" opacity="0.18" />
          <circle cx="475" cy="555" r="13" fill="none" stroke="var(--text-accent)" strokeWidth="1" opacity="0.35" />
          <circle cx="475" cy="555" r="6" fill="var(--fill-accent)" stroke="var(--surface-1)" strokeWidth="2.5" />
          <rect x="497" y="537" width="104" height="34" rx="17" fill="var(--surface-2)" stroke="var(--border)" strokeWidth="0.5" />
          <text x="549" y="558" textAnchor="middle" fill="var(--text-primary)" className="text-[14px] font-medium">Mombasa</text>
        </g>

        <g transform="translate(614,50)">
          <circle cx="0" cy="0" r="18" fill="var(--surface-2)" stroke="var(--border)" strokeWidth="0.5" />
          <line x1="0" y1="-13" x2="0" y2="13" stroke="var(--text-secondary)" strokeWidth="1" />
          <line x1="-13" y1="0" x2="13" y2="0" stroke="var(--text-secondary)" strokeWidth="1" />
          <path d="M 0,-13 L 4,0 L 0,4 L -4,0 Z" fill="var(--text-primary)" />
          <text x="0" y="-22" textAnchor="middle" fill="var(--text-secondary)" className="font-mono text-[10px]">N</text>
        </g>

        <g transform="translate(60,650)">
          <line x1="0" y1="0" x2="60" y2="0" stroke="var(--text-secondary)" strokeWidth="1" />
          <line x1="0" y1="-4" x2="0" y2="4" stroke="var(--text-secondary)" strokeWidth="1" />
          <line x1="60" y1="-4" x2="60" y2="4" stroke="var(--text-secondary)" strokeWidth="1" />
          <text x="30" y="18" textAnchor="middle" fill="var(--text-muted)" className="font-mono text-[10px]">~250 nm</text>
        </g>
      </svg>

      <div className="flex gap-4 mt-1.5 flex-wrap text-xs ink-soft">
        <span className="inline-flex items-center gap-1.5">
          <span className="w-4 border-t-2 border-dashed border-emerald" />
          Remaining route
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="w-4 border-t-2 border-dashed border-[hsla(222,24%,18%,0.45)]" />
          Completed
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-[7px] w-[7px] rounded-full bg-[hsla(222,24%,18%,0.45)]" />
          Port
        </span>
      </div>

      <div className="flex gap-3 mt-2.5">
        <div className="flex-1 bg-white border border-ink-10 rounded-xl px-3 py-2.5">
          <p className="m-0 mb-0.5 text-xs ink-soft">Distance remaining</p>
          <p className="m-0 text-sm font-medium ink">312 nm</p>
        </div>
        <div className="flex-1 bg-white border border-ink-10 rounded-xl px-3 py-2.5">
          <p className="m-0 mb-0.5 text-xs ink-soft">ETA Mombasa</p>
          <p className="m-0 text-sm font-medium ink">Jul 4, 06:40</p>
        </div>
      </div>
    </div>
  );
};
